import { ref } from 'vue';
import { trackBeginCheckout } from '../analytics/events.js';

/**
 * Map a credit-pack checkout endpoint to the GA4 `item` value.
 * Kept here (rather than at each call site) so the two one-click pack
 * buttons -- AnalyzePage's audit credits and ProspectingPage's lead
 * credits -- can never drift apart in the analytics they report.
 */
function checkoutItemFor(endpoint) {
  if (endpoint.includes('lead-credits')) return 'lead_credits';
  if (endpoint.includes('audits')) return 'audit_credits';
  return 'credits';
}

/**
 * Shared "buy a one-time credit pack" checkout button behaviour --
 * ProspectingPage.vue's buyCredits() and AnalyzePage.vue's buyAudits()
 * were two independently hand-copied implementations of the exact same
 * 20-line pattern (guard, fetch, 401 redirect, error handling). Both
 * carried the identical real bug (found 2026-08-31, from a real browser
 * console log showing a single click firing three POST requests):
 * `:disabled="loading"` only takes effect once Vue actually repaints the
 * DOM, which is an async, microtask-batched operation -- it does NOT
 * synchronously stop further @click handler invocations on the
 * not-yet-repainted button in between. A rapid double/triple click
 * (very plausible from someone re-clicking after seeing an error, which
 * is exactly what happened live) fires the handler again before that
 * repaint lands.
 *
 * Extracted into one composable rather than fixing both copies
 * separately -- two independently-maintained copies of the same logic is
 * exactly how this bug pattern gets a chance to drift back in later; one
 * implementation can't drift from itself.
 *
 * @param {object} router - the calling component's `useRouter()` instance.
 * @param {object} route - the calling component's `useRoute()` instance
 *   (used to build the post-login return URL on a 401).
 * @param {(path: string, init?: object) => Promise<Response>} fetchFn -
 *   defaults to window.fetchWithCSRF (the app-wide CSRF-aware fetch
 *   wrapper every other authenticated POST in this codebase uses) --
 *   injectable so tests can supply a mock without needing that global.
 * @returns {{loading: import('vue').Ref<boolean>, error: import('vue').Ref<string>, submit: (endpoint: string) => Promise<void>}}
 */
export function useCheckoutButton(router, route, fetchFn = (...args) => window.fetchWithCSRF(...args)) {
  const loading = ref(false);
  const error = ref('');

  async function submit(endpoint) {
    // THE actual re-entrancy guard -- synchronous, checked before
    // anything else runs. loading.value was already being set
    // synchronously before this fix; it just was never CHECKED first.
    // The :disabled attribute on the calling component's button is
    // belt-and-suspenders on top of this, not sufficient by itself.
    if (loading.value) return;
    error.value = '';
    loading.value = true;
    try {
      const resp = await fetchFn(endpoint, { method: 'POST' });
      if (resp.status === 401) {
        // Session expired mid-visit — same redirect-with-return-url
        // pattern ReportPage.vue's own auth-error handling already uses
        // (CLAUDE.md §8.2).
        router.push(`/login?redirect=${encodeURIComponent(route.fullPath)}`);
        return;
      }
      const data = await resp.json().catch(() => ({}));
      if (resp.ok && data.success && data.url) {
        // GA4 begin_checkout: the session was created and the browser is
        // about to leave for Stripe. Fired here, at the one place every
        // credit-pack button funnels through, so the event can't drift.
        trackBeginCheckout({ item: checkoutItemFor(endpoint) });
        window.location.href = data.url;
        return; // leaving the page — keep the button disabled through the redirect
      }
      error.value = data.error || data.detail || 'Could not start checkout. Please try again.';
    } catch (e) {
      error.value = 'Network error. Please try again.';
    }
    loading.value = false;
  }

  return { loading, error, submit };
}
